import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const MessageList = ({ messages, isTyping, darkMode, systemSettings, messagesEndRef }) => {
  // Get AI assistant settings with fallbacks
  const aiAssistant = systemSettings?.aiAssistant || {
    name: 'Jeje',
    avatarUrl: '/jeje_bot.png',
    description: 'AI Assistant from Scalovate'
  };

  const formatMessage = (text) => {
    // Convert markdown-like formatting to HTML
    return text
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="bg-gray-100 px-1 rounded">$1</code>')
      .replace(/\n/g, '<br>');
  };

  return (
    <div className={`h-full overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 custom-scrollbar ${
      darkMode ? 'bg-gray-900/50' : 'bg-white/30'
    }`}>
      <style jsx>{`
        .custom-scrollbar {
          scroll-behavior: smooth;
          -webkit-overflow-scrolling: touch;
        }
        
        .custom-scrollbar::-webkit-scrollbar {
          width: 8px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: ${darkMode ? 'linear-gradient(to bottom, #374151, #4b5563)' : 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)'};
          border-radius: 10px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: linear-gradient(to bottom, var(--primary-color, #ce6161), var(--secondary-color, #b54545));
          border-radius: 10px;
          border: 2px solid ${darkMode ? '#374151' : '#f1f5f9'};
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(to bottom, var(--secondary-color, #b54545), #9a3a3a);
        }
        
        .custom-scrollbar::-webkit-scrollbar-corner {
          background: ${darkMode ? '#374151' : '#f1f5f9'};
        }
      `}</style>
      
      <AnimatePresence>
        {messages.map((message, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div className={`max-w-[85%] sm:max-w-[80%] md:max-w-[70%] ${
              message.type === 'user' ? 'order-2' : 'order-1'
            }`}>
              {message.type === 'bot' && (
                <div className="flex items-center mb-1 sm:mb-2">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full mr-2 sm:mr-3 overflow-hidden border-2 border-blue-500/30">
                    <img 
                      src={systemSettings?.aiAssistant?.avatarUrl || '/jeje_bot.png'} 
                      alt={`${systemSettings?.aiAssistant?.name || 'Jeje'} Avatar`}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        // Fallback to gradient background if image fails
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                    <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center" style={{ display: 'none' }}>
                      <span className="text-white text-xs sm:text-sm font-bold">
                        {systemSettings?.aiAssistant?.name?.charAt(0) || 'J'}
                      </span>
                    </div>
                  </div>
                  <span className={`text-xs sm:text-sm font-medium ${
                    darkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    {systemSettings?.aiAssistant?.name || 'Jeje'}
                  </span>
                </div>
              )}
              
              <div className={`rounded-lg sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-3 shadow-lg backdrop-blur-sm ${
                message.type === 'user'
                  ? 'bg-blue-600 text-white ml-auto'
                  : darkMode
                  ? 'bg-gray-800/80 text-white border border-gray-700/50'
                  : 'bg-white/80 text-gray-800 border border-gray-200/50'
              }`}>
                <p className="text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words">
                  {message.isStreaming ? (
                    <span>
                      {message.text}
                      <span className="animate-pulse">|</span>
                    </span>
                  ) : (
                    message.text
                  )}
                </p>
                
                <div className={`text-xs mt-1 sm:mt-2 ${
                  message.type === 'user' 
                    ? 'text-blue-100' 
                    : darkMode 
                    ? 'text-gray-400' 
                    : 'text-gray-500'
                }`}>
                  {message.timestamp?.toLocaleTimeString([], { 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
      
      {/* Typing indicator */}
      {(isTyping && !messages.some(m => m.isStreaming)) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="flex justify-start"
        >
          <div className="flex items-center mb-1 sm:mb-2">
            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full mr-2 sm:mr-3 overflow-hidden border-2 border-blue-500/30">
              <img 
                src={systemSettings?.aiAssistant?.avatarUrl || '/jeje_bot.png'} 
                alt={`${systemSettings?.aiAssistant?.name || 'Jeje'} Avatar`}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Fallback to gradient background if image fails
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="w-full h-full bg-gradient-to-r from-blue-500 to-purple-600 flex items-center justify-center" style={{ display: 'none' }}>
                <span className="text-white text-xs sm:text-sm font-bold">
                  {systemSettings?.aiAssistant?.name?.charAt(0) || 'J'}
                </span>
              </div>
            </div>
            <div className={`rounded-lg sm:rounded-2xl px-3 py-2 sm:px-4 sm:py-3 shadow-lg backdrop-blur-sm ${
              darkMode
                ? 'bg-gray-800/80 text-white border border-gray-700/50'
                : 'bg-white/80 text-gray-800 border border-gray-200/50'
            }`}>
              <div className="flex space-x-1">
                <div className={`w-2 h-2 rounded-full animate-bounce ${
                  darkMode ? 'bg-gray-400' : 'bg-gray-600'
                }`} style={{ animationDelay: '0ms' }}></div>
                <div className={`w-2 h-2 rounded-full animate-bounce ${
                  darkMode ? 'bg-gray-400' : 'bg-gray-600'
                }`} style={{ animationDelay: '150ms' }}></div>
                <div className={`w-2 h-2 rounded-full animate-bounce ${
                  darkMode ? 'bg-gray-400' : 'bg-gray-600'
                }`} style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;